/**
 * Main plugin builder editor page.
 * Two-pane layout: Conversation/Guided panels (left) | Preview (right)
 * Supports guided flow (6 phases), verbose events, undo/redo,
 * device sizing, screenshots, and auto-approve during execution.
 * On mobile, panes scroll horizontally.
 */

import { useState, useCallback, useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import { usePluginBuilderSession } from '../hooks/usePluginBuilderSession';
import { PluginConversationPane } from '../components/PluginConversationPane';
import { PluginPreviewPane } from '../components/PluginPreviewPane';
import { PluginPromptInput } from '../components/PluginPromptInput';
import { PluginBuilderHeader } from '../components/PluginBuilderHeader';
import { PluginGuidedWelcome } from '../components/PluginGuidedWelcome';
import { PluginConfigPanel } from '../components/PluginConfigPanel';
import { PluginReportProblemDialog } from '../components/PluginReportProblemDialog';
import { GuidedPhaseBar } from '../components/guided/GuidedPhaseBar';
import { GuidedModeToggle } from '../components/guided/GuidedModeToggle';
import { GuidedInterventionBar } from '../components/guided/GuidedInterventionBar';
import { ClarificationControls } from '../components/guided/ClarificationControls';
import { PlanReview } from '../components/guided/PlanReview';
import { AssumptionReview } from '../components/guided/AssumptionReview';
import { StepExecutionPanel } from '../components/guided/StepExecutionPanel';
import { CompletionBanner } from '../components/guided/CompletionBanner';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, ChevronLeft, ChevronRight, ClipboardList, Square } from 'lucide-react';
import { useTheme } from 'next-themes';
import api from '@/lib/api';
import { VerboseTimeline } from '../../../components/VerboseTimeline';
import type { GuidedPhase, VerboseEvent, LogEntry } from '../types';

export function PluginBuilderPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);
  const { theme: appTheme } = useTheme();

  // ─── Page-level state ───────────────────────────────────────────────────────

  const [inputValue, setInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [suggestedNextSteps, setSuggestedNextSteps] = useState<string[]>([]);
  const [verbose, setVerbose] = useState(false);
  const [deepThinking, setDeepThinking] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const autoApproveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevIsSendingForAutoApproveRef = useRef(false);
  const criteriaAutoContinuedRef = useRef(false);
  const [guidedViewMode, setGuidedViewMode] = useState<'panel' | 'conversation'>('panel');
  const [guidedIdea, setGuidedIdea] = useState('');
  const [guidedName, setGuidedName] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>(
    appTheme === 'light' ? 'light' : 'dark',
  );
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);

  // Sync preview theme when the app theme changes
  useEffect(() => {
    setPreviewTheme(appTheme === 'light' ? 'light' : 'dark');
  }, [appTheme]);

  // ─── Verbose event state (RAF-batched) ──────────────────────────────────────

  const [verboseHistory, setVerboseHistory] = useState<Record<string, VerboseEvent[]>>({});
  const [pendingVerboseEvents, setPendingVerboseEvents] = useState<VerboseEvent[]>([]);
  const pendingVerboseRef = useRef<VerboseEvent[]>([]);
  const prevIsSendingRef = useRef(false);
  const flushRafRef = useRef<number | null>(null) as MutableRefObject<number | null>;

  const handleVerboseEvent = useCallback((event: VerboseEvent) => {
    if (event.type === 'model_info') {
      pendingVerboseRef.current = [event];
    } else {
      pendingVerboseRef.current = [...pendingVerboseRef.current, event];
    }
    if (flushRafRef.current === null) {
      flushRafRef.current = requestAnimationFrame(() => {
        setPendingVerboseEvents([...pendingVerboseRef.current]);
        flushRafRef.current = null;
      });
    }
  }, []);

  // ─── Session hook ───────────────────────────────────────────────────────────

  const {
    session,
    isLoading,
    error,
    files,
    messages,
    streamingText,
    isSending,
    guidedFlow,
    buildPlan,
    builderMode,
    isGuidedLoading,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    undo,
    redo,
    loadSession,
    sendMessage,
    startGuidedFlow,
    setBuilderMode,
    saveSession,
    resetContext,
    stopGeneration,
    uploadPreviewScreenshot,
    submitAnswers,
    approvePlan,
    requestPlanChanges,
    approveAssumptions,
    suggestAssumptionChanges,
    applyPendingGuidedResult,
    hasPendingGuidedResult,
    cancelGuidedRequest,
    rewindToPhase,
    buildPreview,
    previewHtml,
    isBuildingPreview,
    buildError,
    isResetting,
    setSessionName,
  } = usePluginBuilderSession({
    verbose,
    useOpus: deepThinking,
    onVerboseEvent: handleVerboseEvent,
    onError: (err) => {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
        duration: 8000,
      });
    },
  });

  // ─── Derived state ──────────────────────────────────────────────────────────

  const guidedPhase: GuidedPhase = guidedFlow?.phase ?? 'ideation';
  const isGuidedMode = builderMode === 'guided';

  const isSimpleBuild =
    buildPlan != null && (buildPlan.graph?.nodes?.length ?? buildPlan.steps.length) <= 1;

  const allStepsVerified =
    buildPlan?.approved &&
    (buildPlan.graph
      ? buildPlan.graph.nodes.every((n) => n.status === 'verified')
      : buildPlan.steps.length > 0 && buildPlan.steps.every((s) => s.status === 'verified'));
  const hasPendingCriteria =
    allStepsVerified &&
    (buildPlan?.completionCriteria ?? []).length > 0 &&
    (buildPlan?.completionCriteria ?? []).some((c) => !c.verified);
  const showRefineState =
    guidedPhase === 'complete' || (guidedPhase === 'execution' && allStepsVerified && !isSending);

  // ─── Effects ────────────────────────────────────────────────────────────────

  // Load session on mount
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
  }, [sessionId, loadSession]);

  // Seed guided idea/name from server data when session loads
  useEffect(() => {
    if (guidedFlow?.ideaSummary) {
      setGuidedIdea((prev) => prev || guidedFlow.ideaSummary);
    }
  }, [guidedFlow?.ideaSummary]);

  useEffect(() => {
    if (session?.currentName) {
      setGuidedName((prev) => prev || session.currentName || '');
    }
  }, [session?.currentName]);

  // Auto-determine builder mode from session state on load
  useEffect(() => {
    if (session && guidedFlow) {
      // Keep guided mode for all phases including complete (refine state)
      setBuilderMode('guided');
    } else if (session && session.conversationHistory.length > 0 && !guidedFlow) {
      setBuilderMode('chat');
    }
  }, [session?.sessionId, guidedFlow?.phase]);

  // Auto-switch to panel view when entering a phase that has a guided panel
  const prevGuidedPhaseRef = useRef(guidedPhase);
  useEffect(() => {
    if (guidedPhase !== prevGuidedPhaseRef.current) {
      const prev = prevGuidedPhaseRef.current;
      prevGuidedPhaseRef.current = guidedPhase;
      if (
        guidedPhase === 'clarification' ||
        guidedPhase === 'planning' ||
        guidedPhase === 'assumptions'
      ) {
        setGuidedViewMode('panel');
      }
      // Auto-switch to conversation view when entering complete (refine) phase
      if (guidedPhase === 'complete') {
        setGuidedViewMode('conversation');
      }
      // Auto-start building when transitioning into execution after assumption approval
      if (
        prev === 'assumptions' &&
        guidedPhase === 'execution' &&
        buildPlan?.approved &&
        !isSending
      ) {
        const executionMessage = isSimpleBuild
          ? 'Build the complete plugin now.'
          : 'Begin building the approved plan. Build everything needed, verifying each step as you go.';
        sendMessage(executionMessage, 'execution');
      }
    }
  }, [guidedPhase, buildPlan?.approved, isSending, sendMessage, isSimpleBuild]);

  // Auto-switch back to panel when AI finishes responding during planning phase
  // (skip when a pending guided result is active — user needs to see conversation + Continue button)
  // Also auto-switch to conversation when entering refine state during execution
  useEffect(() => {
    if (prevIsSendingRef.current && !isSending) {
      if (
        !hasPendingGuidedResult &&
        isGuidedMode &&
        guidedPhase === 'planning' &&
        buildPlan &&
        guidedViewMode === 'conversation'
      ) {
        setGuidedViewMode('panel');
      }
      // Switch to conversation view when build completes (refine state)
      if (isGuidedMode && guidedPhase === 'execution' && allStepsVerified) {
        setGuidedViewMode('conversation');
      }
    }
  }, [
    isSending,
    isGuidedMode,
    guidedPhase,
    buildPlan,
    guidedViewMode,
    hasPendingGuidedResult,
    allStepsVerified,
  ]);

  // Keep a ref to messages for use in the verbose commit effect
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Commit pending verbose events when streaming transitions from active to idle
  const prevIsGuidedLoadingRef = useRef(false);
  useEffect(() => {
    const wasSending = prevIsSendingRef.current && !isSending;
    const wasGuidedLoading = prevIsGuidedLoadingRef.current && !isGuidedLoading;
    if (wasSending || wasGuidedLoading) {
      const events = pendingVerboseRef.current;
      if (events.length > 0) {
        const lastMsg = messagesRef.current[messagesRef.current.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          setVerboseHistory((prev) => ({
            ...prev,
            [lastMsg.timestamp || String(Date.now())]: events,
          }));
        }
      }
      pendingVerboseRef.current = [];
      setPendingVerboseEvents([]);
    }
    prevIsSendingRef.current = isSending;
    prevIsGuidedLoadingRef.current = isGuidedLoading;
  }, [isSending, isGuidedLoading]);

  // Auto-approve effect: when auto-approve is ON and AI finishes a step
  useEffect(() => {
    const wasSending = prevIsSendingForAutoApproveRef.current;
    prevIsSendingForAutoApproveRef.current = isSending;

    if (!autoApprove || !isGuidedMode || guidedPhase !== 'execution') return;

    if (!isSending && wasSending) {
      autoApproveTimerRef.current = setTimeout(() => {
        sendMessage(
          'Continue. If the current step is fully built, verify and advance to the next step.',
        );
      }, 2000);
    }

    return () => {
      if (autoApproveTimerRef.current) {
        clearTimeout(autoApproveTimerRef.current);
        autoApproveTimerRef.current = null;
      }
    };
  }, [isSending, autoApprove, isGuidedMode, guidedPhase, sendMessage]);

  // Auto-continue once when all steps verified but criteria still pending
  // (fires on isSending transition from true→false, checked via prevIsSendingForAutoApproveRef
  // which is set in the auto-approve effect above before this effect runs)
  useEffect(() => {
    if (
      !isSending &&
      guidedPhase === 'execution' &&
      allStepsVerified &&
      hasPendingCriteria &&
      !criteriaAutoContinuedRef.current
    ) {
      criteriaAutoContinuedRef.current = true;
      sendMessage('Continue. Address the remaining completion criteria.', 'execution');
    }
  }, [isSending, guidedPhase, allStepsVerified, hasPendingCriteria, sendMessage]);

  // Listen for LOG_ENTRY from preview iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'LOG_ENTRY' && event.data.entry) {
        setLogEntries((prev) => [...prev, event.data.entry as LogEntry]);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Cleanup RAF and timers on unmount
  useEffect(() => {
    return () => {
      if (flushRafRef.current !== null) {
        cancelAnimationFrame(flushRafRef.current);
      }
      if (autoApproveTimerRef.current) {
        clearTimeout(autoApproveTimerRef.current);
      }
    };
  }, []);

  // Track which pane was last clicked for scoped Ctrl+A selection
  const conversationPaneRef = useRef<HTMLDivElement>(null);
  const previewPaneRef = useRef<HTMLDivElement>(null);
  const activePaneRef = useRef<HTMLDivElement | null>(null);

  const handlePaneFocus = useCallback((paneRef: React.RefObject<HTMLDivElement | null>) => {
    activePaneRef.current = paneRef.current;
  }, []);

  // Keyboard shortcuts for undo/redo and scoped Ctrl+A
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + A: scope selection to the active pane
      if (modifier && e.key.toLowerCase() === 'a') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        if (activePaneRef.current) {
          e.preventDefault();
          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNodeContents(activePaneRef.current);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }

      // Ctrl/Cmd + Z (undo) or Ctrl/Cmd + Shift + Z (redo)
      if (modifier && e.key.toLowerCase() === 'z') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }

      // Ctrl/Cmd + Y for redo (Windows convention)
      if (modifier && e.key.toLowerCase() === 'y') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // ─── Mobile horizontal scroll ──────────────────────────────────────────────

  const panelsContainerRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [scrollState, setScrollState] = useState({ scrollLeft: 0, scrollWidth: 0, clientWidth: 0 });

  useEffect(() => {
    const container = panelsContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setScrollState({
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    };

    const scheduleSnap = () => {
      if (isDraggingRef.current) return;
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = setTimeout(() => {
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (maxScroll <= 0) return;
        const ratio = container.scrollLeft / maxScroll;
        const target = ratio > 0.5 ? maxScroll : 0;
        container.scrollTo({ left: target, behavior: 'smooth' });
      }, 150);
    };

    const handleScroll = () => {
      updateScrollState();
      scheduleSnap();
    };

    updateScrollState();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver.disconnect();
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    };
  }, [isLoading]);

  const handleScrollbarDrag = useCallback((clientX: number) => {
    const container = panelsContainerRef.current;
    const track = scrollTrackRef.current;
    if (!container || !track) return;

    const trackRect = track.getBoundingClientRect();
    const trackWidth = trackRect.width;
    const thumbWidth = 24;
    const maxThumbLeft = trackWidth - thumbWidth;

    const relativeX = clientX - trackRect.left - thumbWidth / 2;
    const clampedX = Math.max(0, Math.min(relativeX, maxThumbLeft));

    const scrollRatio = clampedX / maxThumbLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    container.scrollLeft = scrollRatio * maxScrollLeft;
  }, []);

  const scrollToPanel = useCallback((panel: 'left' | 'right') => {
    const container = panelsContainerRef.current;
    if (!container) return;
    const target = panel === 'left' ? 0 : container.scrollWidth - container.clientWidth;
    container.scrollTo({ left: target, behavior: 'smooth' });
  }, []);

  // ─── Callbacks ──────────────────────────────────────────────────────────────

  const handlePromptSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || isSending) return;
    setInputValue('');
    sendMessage(
      trimmed,
      isGuidedMode && guidedFlow ? (showRefineState ? 'complete' : guidedPhase) : undefined,
    );
  }, [inputValue, isSending, sendMessage, isGuidedMode, guidedPhase, guidedFlow, showRefineState]);

  const handleSave = useCallback(
    async (name: string, description?: string) => {
      setIsSaving(true);
      try {
        await saveSession(name, description);
        toast({
          title: 'Saved',
          description: 'Plugin saved as draft.',
        });
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to save plugin.',
          variant: 'destructive',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [saveSession, toast],
  );

  const handlePublish = useCallback(async () => {
    if (!sessionId) return;
    setIsPublishing(true);
    try {
      const result = await api.publishPluginBuilder(sessionId);
      toast({
        title: 'Plugin Submitted',
        description: `Submission ${result.submissionId} is under review. You'll be notified when approved.`,
      });
    } catch (err) {
      toast({
        title: 'Publish Failed',
        description: (err as Error).message,
        variant: 'destructive',
        duration: 8000,
      });
    } finally {
      setIsPublishing(false);
    }
  }, [sessionId, toast]);

  const handleResetContext = useCallback(async () => {
    await resetContext();
    setSuggestedNextSteps([]);
    setVerboseHistory({});
    setPendingVerboseEvents([]);
    pendingVerboseRef.current = [];
    if (flushRafRef.current !== null) {
      cancelAnimationFrame(flushRafRef.current);
      flushRafRef.current = null;
    }
    toast({
      title: 'Context Reset',
      description: 'Conversation history cleared. Your plugin files have been preserved.',
    });
  }, [resetContext, toast]);

  const handleSettingsChange = useCallback(
    (settings: unknown) => {
      if (!sessionId) return;
      api.updatePluginBuilderSession(sessionId, { currentSettings: settings });
    },
    [sessionId],
  );

  // Called from PluginConfigPanel when the plugin name is saved.
  // Optimistically updates the header and persists to the currentName column.
  const handleConfigNameChange = useCallback(
    (newName: string) => {
      setSessionName(newName);
      if (sessionId) {
        api.updatePluginBuilderSession(sessionId, { currentName: newName });
      }
    },
    [sessionId, setSessionName],
  );

  // ─── Guided flow callbacks ──────────────────────────────────────────────────

  const handleGuidedStart = useCallback(
    async (idea: string, name?: string) => {
      if (!sessionId) return;
      try {
        if (name) {
          // Optimistically update the header right away
          setSessionName(name);
          await api.updatePluginBuilderSession(sessionId, { currentName: name });
        }
        setGuidedViewMode('conversation');
        await startGuidedFlow(idea);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [sessionId, setSessionName, startGuidedFlow, toast],
  );

  const handleSubmitAnswers = useCallback(
    async (answers: Record<number, string>, comment?: string) => {
      try {
        setGuidedViewMode('conversation');
        await submitAnswers(answers, comment);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [submitAnswers, toast],
  );

  const handlePlanReviewApprove = useCallback(async () => {
    try {
      setGuidedViewMode('conversation');
      await approvePlan();
    } catch (err) {
      toast({
        title: 'Error',
        description: (err as Error).message,
        variant: 'destructive',
        duration: 8000,
      });
    }
  }, [approvePlan, toast]);

  const handlePlanReviewChanges = useCallback(
    async (feedback: string) => {
      try {
        await requestPlanChanges(feedback);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [requestPlanChanges, toast],
  );

  const handleSuggestAssumptionChanges = useCallback(
    async (comments: Record<string, string>) => {
      try {
        await suggestAssumptionChanges(comments);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [suggestAssumptionChanges, toast],
  );

  const handleApprovePlan = useCallback(
    async (comments?: Record<string, string>) => {
      try {
        if (comments && Object.keys(comments).length > 0) {
          await suggestAssumptionChanges(comments);
        }
        await approveAssumptions();
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
          duration: 8000,
        });
      }
    },
    [approveAssumptions, suggestAssumptionChanges, toast],
  );

  const handleContinueTransition = useCallback(() => {
    applyPendingGuidedResult();
  }, [applyPendingGuidedResult]);

  const handlePhaseClick = useCallback(
    async (phase: GuidedPhase) => {
      try {
        await rewindToPhase(phase);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [rewindToPhase, toast],
  );

  const handleExecutionFeedback = useCallback(
    async (message: string) => {
      await sendMessage(message, guidedPhase);
    },
    [sendMessage, guidedPhase],
  );

  const handleStopFromPanel = useCallback(() => {
    stopGeneration();
    setGuidedViewMode('conversation');
  }, [stopGeneration]);

  // ─── Dynamic placeholder ────────────────────────────────────────────────────

  const guidedPlaceholder = useMemo(() => {
    if (!isGuidedMode) return undefined;
    switch (guidedPhase) {
      case 'ideation':
        return 'Describe what plugin you want to build...';
      case 'clarification':
        return "Answer the AI's questions...";
      case 'planning':
        return 'Review the plan or request changes...';
      case 'execution':
        return autoApprove ? 'Auto-approving steps...' : 'Give feedback or continue...';
      case 'complete':
        return 'Continue chatting to refine your plugin...';
      default:
        return undefined;
    }
  }, [isGuidedMode, guidedPhase, autoApprove]);

  // ─── Early returns ──────────────────────────────────────────────────────────

  if (!sessionId) {
    navigate('/a/arche_plugin_builder');
    return null;
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load session</p>
        <Button onClick={() => navigate('/a/arche_plugin_builder')}>Back to Sessions</Button>
      </div>
    );
  }

  // ─── Prompt input props ─────────────────────────────────────────────────────

  const hasMessages = messages.length > 0;

  const promptInputProps = {
    value: inputValue,
    onChange: setInputValue,
    onSubmit: handlePromptSubmit,
    onStopGeneration: stopGeneration,
    onResetContext: handleResetContext,
    isProcessing: isSending,
    isResetting,
    hasMessages,
    placeholder: guidedPlaceholder,
  };

  // ─── Guided panel detection ─────────────────────────────────────────────────

  const clarifyingQA = guidedFlow?.clarifyingQA ?? [];
  const hasClarificationPanel =
    isGuidedMode && guidedPhase === 'clarification' && clarifyingQA.length > 0;
  const hasPlanningPanel = isGuidedMode && guidedPhase === 'planning' && buildPlan !== undefined;
  const isPlanGenerating =
    isGuidedMode &&
    guidedPhase === 'planning' &&
    buildPlan === undefined &&
    (isSending || isGuidedLoading);
  const hasAssumptionsPanel =
    isGuidedMode &&
    guidedPhase === 'assumptions' &&
    ((guidedFlow?.assumptions ?? []).length > 0 || buildPlan !== undefined);

  // Detect guided loading states that show a full-panel loading indicator with cancel
  const isGuidedLoadingWithCancel =
    isGuidedMode &&
    isGuidedLoading &&
    !streamingText && // Don't show spinner overlay when text is streaming
    !hasClarificationPanel &&
    !hasPlanningPanel &&
    !hasAssumptionsPanel;

  const guidedLoadingMessage = (() => {
    if (!isGuidedLoadingWithCancel) return { title: '', subtitle: '' };
    switch (guidedPhase) {
      case 'ideation':
        return {
          title: 'Generating questions...',
          subtitle: 'Analyzing your idea and preparing clarifying questions.',
        };
      case 'clarification':
        return {
          title: 'Generating build plan...',
          subtitle: 'Researching patterns and designing the steps for your plugin.',
        };
      case 'planning':
        return {
          title: 'Generating...',
          subtitle: 'Researching patterns and designing the steps for your plugin.',
        };
      case 'assumptions':
        return {
          title: 'Revising assumptions...',
          subtitle: 'Updating assumptions based on your feedback.',
        };
      default:
        return {
          title: 'Processing...',
          subtitle: 'Please wait while the AI works on your request.',
        };
    }
  })();

  const hasGuidedPanel =
    hasClarificationPanel ||
    hasPlanningPanel ||
    isPlanGenerating ||
    hasAssumptionsPanel ||
    isGuidedLoadingWithCancel;
  const showGuidedPanel = hasGuidedPanel && guidedViewMode === 'panel';

  // Hide the full prompt input and show the intervention bar instead
  const hidePromptInput =
    hasPendingGuidedResult ||
    (isGuidedMode &&
      (showGuidedPanel ||
        (guidedPhase === 'ideation' && !hasMessages) ||
        isGuidedLoading ||
        (guidedPhase === 'execution' && !showRefineState)));

  const panelToggleLabel = hasClarificationPanel
    ? 'Questions'
    : hasPlanningPanel || isPlanGenerating
      ? 'Plan'
      : hasAssumptionsPanel
        ? 'Review'
        : isGuidedLoadingWithCancel
          ? 'Loading'
          : '';

  // ─── Guided content for ConversationPane slots ──────────────────────────────

  const guidedEmptyContent =
    isGuidedMode && guidedPhase === 'ideation' && !hasMessages ? (
      <PluginGuidedWelcome
        onStart={handleGuidedStart}
        isProcessing={isGuidedLoading}
        idea={guidedIdea}
        onIdeaChange={setGuidedIdea}
        name={guidedName}
        onNameChange={setGuidedName}
      />
    ) : undefined;

  const guidedHeaderContent = isGuidedMode ? (
    showRefineState ? (
      <CompletionBanner buildPlan={buildPlan ?? null} />
    ) : null
  ) : null;

  const guidedFooterContent =
    isGuidedMode &&
    guidedPhase === 'execution' &&
    buildPlan?.approved &&
    !showRefineState &&
    !isSimpleBuild ? (
      <StepExecutionPanel
        buildPlan={buildPlan}
        autoApprove={autoApprove}
        onAutoApproveChange={setAutoApprove}
        onStopGeneration={stopGeneration}
        onSendFeedback={handleExecutionFeedback}
        isProcessing={isSending}
      />
    ) : null;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <PluginBuilderHeader
        session={session}
        files={files}
        onSave={handleSave}
        onPublish={handlePublish}
        onConfigure={() => setConfigDialogOpen(true)}
        onReportProblem={() => setReportDialogOpen(true)}
        isSaving={isSaving}
        isPublishing={isPublishing}
        isAdmin={isAdmin}
      />

      {/* Main content wrapper */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Guided phase bar */}
        {isGuidedMode && guidedFlow && (
          <GuidedPhaseBar
            currentPhase={showRefineState ? 'complete' : guidedPhase}
            buildPlan={buildPlan}
            onPhaseClick={handlePhaseClick}
          />
        )}

        {/* Scroll container */}
        <div
          ref={panelsContainerRef}
          className="flex-1 min-h-0 overflow-x-auto md:overflow-x-hidden overflow-y-hidden scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Inner row */}
          <div className="h-full flex flex-row w-[200vw] md:w-full">
            {/* Left: Conversation or guided panel */}
            <div
              ref={conversationPaneRef}
              onMouseDown={() => handlePaneFocus(conversationPaneRef)}
              className="w-[100vw] md:w-1/2 border-r flex flex-col min-h-0"
            >
              {/* Chat pane header — matches preview pane header height */}
              <div className="flex items-center border-b bg-muted/20 px-3 py-1.5 gap-1 flex-shrink-0 min-h-[41px]">
                <GuidedModeToggle mode={builderMode} onModeChange={setBuilderMode} />
              </div>

              {/* Panel/Chat view toggle — shown when a guided panel is available */}
              {hasGuidedPanel && (
                <div className="flex items-center border-b bg-muted/20 px-3 py-1.5 gap-1 flex-shrink-0">
                  <button
                    onClick={() => setGuidedViewMode('panel')}
                    className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${guidedViewMode === 'panel' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {panelToggleLabel}
                  </button>
                  <button
                    onClick={() => setGuidedViewMode('conversation')}
                    className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${guidedViewMode === 'conversation' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Chat
                  </button>
                </div>
              )}

              {/* Full-panel guided content */}
              {showGuidedPanel && hasClarificationPanel ? (
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                  {verbose &&
                    (() => {
                      const lastAssistant = [...messages]
                        .reverse()
                        .find((m) => m.role === 'assistant');
                      const events = lastAssistant
                        ? verboseHistory[lastAssistant.timestamp || ''] || []
                        : [];
                      return events.length > 0 ? (
                        <div className="px-4 pt-4">
                          <VerboseTimeline events={events} />
                        </div>
                      ) : null;
                    })()}
                  <ClarificationControls
                    clarifyingQA={clarifyingQA}
                    onSubmitAnswers={handleSubmitAnswers}
                    isProcessing={isGuidedLoading}
                    onStop={cancelGuidedRequest}
                  />
                </div>
              ) : showGuidedPanel && (isPlanGenerating || isGuidedLoadingWithCancel) ? (
                <div className="flex-1 flex flex-col p-6 space-y-4 overflow-y-auto">
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <ClipboardList className="h-8 w-8 text-muted-foreground/40" />
                      <Loader2 className="h-4 w-4 animate-spin text-primary absolute -bottom-1 -right-1" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">
                        {isGuidedLoadingWithCancel
                          ? guidedLoadingMessage.title
                          : 'Generating your build plan...'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {isGuidedLoadingWithCancel
                          ? guidedLoadingMessage.subtitle
                          : 'Researching patterns and designing the steps for your plugin.'}
                      </p>
                    </div>
                  </div>
                  {verbose && pendingVerboseEvents.length > 0 && (
                    <VerboseTimeline events={pendingVerboseEvents} isPending />
                  )}
                  {isGuidedLoading && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={cancelGuidedRequest}
                      className="w-fit"
                    >
                      <Square className="h-3.5 w-3.5 mr-2 fill-current" />
                      Stop
                    </Button>
                  )}
                </div>
              ) : showGuidedPanel && hasPlanningPanel ? (
                <PlanReview
                  buildPlan={buildPlan!}
                  onApprove={handlePlanReviewApprove}
                  onRequestChanges={handlePlanReviewChanges}
                  isProcessing={isGuidedLoading}
                />
              ) : showGuidedPanel && hasAssumptionsPanel ? (
                <AssumptionReview
                  assumptions={guidedFlow?.assumptions ?? []}
                  buildPlan={buildPlan ?? null}
                  onSuggestChanges={handleSuggestAssumptionChanges}
                  onApprovePlan={handleApprovePlan}
                  isProcessing={isGuidedLoading}
                />
              ) : (
                <PluginConversationPane
                  messages={messages}
                  isProcessing={isSending || isGuidedLoading}
                  streamingText={streamingText}
                  suggestedNextSteps={suggestedNextSteps}
                  verboseHistory={verbose ? verboseHistory : undefined}
                  pendingVerboseEvents={verbose ? pendingVerboseEvents : undefined}
                  onSuggestionClick={setInputValue}
                  emptyStateContent={guidedEmptyContent}
                  headerContent={guidedHeaderContent}
                  footerContent={guidedFooterContent}
                  isAdmin={isAdmin}
                  className="flex-1 min-h-0"
                />
              )}

              {/* Desktop-only: prompt input or intervention bar */}
              {hidePromptInput ? (
                <div className="hidden md:block">
                  {hasPendingGuidedResult ? (
                    <div className="border-t px-4 py-2">
                      <Button onClick={handleContinueTransition} size="sm" className="gap-2">
                        Continue
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : isGuidedLoading ? (
                    <div className="border-t px-4 py-2">
                      <Button
                        onClick={cancelGuidedRequest}
                        variant="destructive"
                        size="sm"
                        className="gap-2"
                      >
                        <Square className="h-3.5 w-3.5 fill-current" />
                        Stop
                      </Button>
                    </div>
                  ) : guidedFooterContent ? null : (
                    <GuidedInterventionBar
                      isProcessing={isSending}
                      onStop={handleStopFromPanel}
                      onSendFeedback={handleExecutionFeedback}
                    />
                  )}
                </div>
              ) : (
                <div className="hidden md:block">
                  <PluginPromptInput {...promptInputProps} />
                </div>
              )}
            </div>

            {/* Right: Preview */}
            <div
              ref={previewPaneRef}
              onMouseDown={() => handlePaneFocus(previewPaneRef)}
              className="w-[100vw] md:w-1/2 flex flex-col min-h-0"
            >
              <PluginPreviewPane
                previewHtml={previewHtml}
                files={files}
                isBuildingPreview={isBuildingPreview}
                buildError={buildError}
                onBuildPreview={() => buildPreview(files ?? undefined)}
                onUndo={undo}
                onRedo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                undoCount={undoCount}
                redoCount={redoCount}
                verbose={verbose}
                onVerboseChange={setVerbose}
                deepThinking={deepThinking}
                onDeepThinkingChange={setDeepThinking}
                isAdmin={isAdmin}
                processGraph={buildPlan?.graph}
                currentNodeId={buildPlan?.currentNodeId}
                logEntries={logEntries}
                onClearLogs={() => setLogEntries([])}
                onScreenshotCaptured={uploadPreviewScreenshot}
                previewTheme={previewTheme}
                onPreviewThemeToggle={() =>
                  setPreviewTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
                }
                className="flex-1 min-h-0"
              />
            </div>
          </div>
        </div>

        {/* Mobile-only slider with nav buttons */}
        <div className="md:hidden flex-shrink-0 border-t border-border bg-muted/30 px-10 py-2 flex items-center gap-1">
          <button
            onClick={() => scrollToPanel('left')}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>

          <div className="flex-1 max-w-[70%] mx-auto">
            <div
              ref={scrollTrackRef}
              className="relative h-6 cursor-pointer flex items-center"
              onMouseDown={(e) => {
                e.preventDefault();
                isDraggingRef.current = true;
                handleScrollbarDrag(e.clientX);

                const handleMouseMove = (moveEvent: MouseEvent) => {
                  handleScrollbarDrag(moveEvent.clientX);
                };
                const handleMouseUp = () => {
                  isDraggingRef.current = false;
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                isDraggingRef.current = true;
                const touch = e.touches[0];
                handleScrollbarDrag(touch.clientX);

                const handleTouchMove = (moveEvent: TouchEvent) => {
                  const moveTouch = moveEvent.touches[0];
                  handleScrollbarDrag(moveTouch.clientX);
                };
                const handleTouchEnd = () => {
                  isDraggingRef.current = false;
                  document.removeEventListener('touchmove', handleTouchMove);
                  document.removeEventListener('touchend', handleTouchEnd);
                };
                document.addEventListener('touchmove', handleTouchMove, { passive: false });
                document.addEventListener('touchend', handleTouchEnd);
              }}
            >
              <div className="absolute inset-x-0 h-1 bg-muted rounded-full" />
              <div
                className="absolute w-6 h-6 bg-primary/80 hover:bg-primary rounded-full transition-colors shadow-sm"
                style={{
                  left: (() => {
                    const maxScroll = scrollState.scrollWidth - scrollState.clientWidth;
                    if (maxScroll <= 0) return '0px';
                    const ratio = scrollState.scrollLeft / maxScroll;
                    return `calc(${ratio * 100}% - ${ratio * 24}px)`;
                  })(),
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            </div>
          </div>

          <button
            onClick={() => scrollToPanel('right')}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Mobile-only: prompt input or intervention bar */}
        {hidePromptInput ? (
          <div className="md:hidden">
            {hasPendingGuidedResult ? (
              <div className="border-t px-4 py-2">
                <Button onClick={handleContinueTransition} size="sm" className="gap-2">
                  Continue
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : isGuidedLoading ? (
              <div className="border-t px-4 py-2">
                <Button
                  onClick={cancelGuidedRequest}
                  variant="destructive"
                  size="sm"
                  className="gap-2"
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                  Stop
                </Button>
              </div>
            ) : guidedFooterContent ? null : (
              <GuidedInterventionBar
                isProcessing={isSending}
                onStop={handleStopFromPanel}
                onSendFeedback={handleExecutionFeedback}
              />
            )}
          </div>
        ) : (
          <div className="md:hidden">
            <PluginPromptInput {...promptInputProps} />
          </div>
        )}
      </div>

      {/* Configure Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Plugin Settings</DialogTitle>
            <DialogDescription>
              Configure your plugin&apos;s name, pricing, visibility, and theme.
            </DialogDescription>
          </DialogHeader>
          <PluginConfigPanel
            session={session}
            onSettingsChange={handleSettingsChange}
            onNameChange={handleConfigNameChange}
            className="py-4"
          />
        </DialogContent>
      </Dialog>

      {/* Report Problem Dialog */}
      <PluginReportProblemDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        session={session}
        files={files}
        logEntries={logEntries}
        guidedFlow={guidedFlow}
        buildPlan={buildPlan}
        guidedBuilderState={{ mode: builderMode, phase: guidedPhase, autoApprove }}
      />
    </div>
  );
}
